import { getRequestConfig, withMakeRequest } from '~/api';
import Request from '~/api/Request.ts';
import {
  CreateRoleRequest,
  CreateRoleResponse,
  DeleteRoleRequest,
  UpdateDirectoryRoleMappingRequest,
  GetOrganizationRoleListRequest,
  GetRoleListResponse,
  RoleData,
  UpdateRoleRequest,
  UpdateRoleResponse
} from '~/api/types/roleData.ts';

const transformRolesWithPermissionsResponse = (
  data: GetRoleListResponse
): RoleData[] => {
  return data.roles.map((role) => {
    const parent = data.roles.find((r) => r.name === role.extends)?.displayName;
    return {
      ...role,
      ...(parent ? { parentRoleDisplayName: parent } : {})
    };
  });
};

const getEnvironmentRoles: () => Promise<RoleData[]> = withMakeRequest(
  async () =>
    await Request.getInstance().get(
      `/roles`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetRoleListResponse) => data.roles
);

const getEnvironmentRolesWithPermissions: (request?: {
  includeAllPermissions: boolean;
}) => Promise<RoleData[]> = withMakeRequest(
  async ({ includeAllPermissions } = {}) =>
    await Request.getInstance().get(
      `/roles?include=${includeAllPermissions ? 'permissions:all' : 'permissions'}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  transformRolesWithPermissionsResponse
);

type GetEnvironmentRoleDetailsRequest = Pick<RoleData, 'name'> & {
  includeAllPermissions?: boolean;
};

type GetEnvironmentRoleDetailsResponse = {
  role: RoleData;
};

const getEnvironmentRoleDetails: (
  request: GetEnvironmentRoleDetailsRequest
) => Promise<RoleData> = withMakeRequest(
  async ({ name, includeAllPermissions }: GetEnvironmentRoleDetailsRequest) =>
    await Request.getInstance().get(
      `/roles/${name}${includeAllPermissions ? '?include=permissions:all' : ''}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetEnvironmentRoleDetailsResponse) => data.role
);

const getOrganizationRoles: (
  request: GetOrganizationRoleListRequest
) => Promise<RoleData[]> = withMakeRequest(
  async ({ organizationId }: GetOrganizationRoleListRequest) =>
    await Request.getInstance().get(
      `/organizations/${organizationId ?? '-'}/roles`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetRoleListResponse) => data.roles
);

const getOrganizationRolesWithPermissions: (request?: {
  orgId: string;
  includeAllPermissions: boolean;
}) => Promise<RoleData[]> = withMakeRequest(
  async ({ orgId, includeAllPermissions } = {}) =>
    await Request.getInstance().get(
      `/organizations/${orgId}/roles?include=${includeAllPermissions ? 'permissions:all' : 'permissions'}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  transformRolesWithPermissionsResponse
);

type GetOrganizationRoleDetailsRequest = Pick<RoleData, 'name'> & {
  orgId: string;
  includeAllPermissions?: boolean;
};

const getOrganizationRoleDetails: (
  request: GetOrganizationRoleDetailsRequest
) => Promise<RoleData> = withMakeRequest(
  async ({
    name,
    orgId,
    includeAllPermissions
  }: GetOrganizationRoleDetailsRequest) =>
    await Request.getInstance().get(
      `/organizations/${orgId}/roles/${name}${includeAllPermissions ? '?include=permissions:all' : ''}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: GetEnvironmentRoleDetailsResponse) => data.role
);

const createEnvironmentRole: (request: CreateRoleRequest) => Promise<RoleData> =
  withMakeRequest(
    async (request: CreateRoleRequest) =>
      await Request.getInstance().post(
        `/roles`,
        request,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: CreateRoleResponse) => data.role
  );

export type CreateOrganizationRoleRequest = CreateRoleRequest & {
  orgId: string;
};

const createOrganizationRole: (
  request: CreateOrganizationRoleRequest
) => Promise<RoleData> = withMakeRequest(
  async ({ orgId, ...payload }: CreateOrganizationRoleRequest) =>
    await Request.getInstance().post(
      `/organizations/${orgId}/roles`,
      payload,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: CreateRoleResponse) => data.role
);

const updateEnvironmentRole: (request: UpdateRoleRequest) => Promise<RoleData> =
  withMakeRequest(
    async ({ name: roleName, ...payload }: UpdateRoleRequest) =>
      await Request.getInstance().put(
        `/roles/${roleName}`,
        payload,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: UpdateRoleResponse) => data.role
  );

export type UpdateOrganizationRoleRequest = UpdateRoleRequest & {
  orgId: string;
};

const updateOrganizationRole: (
  request: UpdateOrganizationRoleRequest
) => Promise<RoleData> = withMakeRequest(
  async ({
    orgId,
    name: roleName,
    ...payload
  }: UpdateOrganizationRoleRequest) =>
    await Request.getInstance().put(
      `/organizations/${orgId}/roles/${roleName}`,
      payload,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdateRoleResponse) => data.role
);

type UpdateDefaultOrganizationRoleRequest = {
  orgId: string;
  defaultMemberRole: string;
};

type UpdateDefaultOrganizationRoleResponse = {
  defaultMember: RoleData;
};

const updateDefaultOrganizationRole: (
  request: UpdateDefaultOrganizationRoleRequest
) => Promise<RoleData> = withMakeRequest(
  async ({ orgId, defaultMemberRole }: UpdateDefaultOrganizationRoleRequest) =>
    await Request.getInstance().patch(
      `/organizations/${orgId}/roles:set_defaults`,
      { defaultMemberRole },
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: UpdateDefaultOrganizationRoleResponse) => data.defaultMember
);

const deleteEnvironmentRole: (request: DeleteRoleRequest) => Promise<boolean> =
  withMakeRequest(
    async ({ roleName }: DeleteRoleRequest) =>
      await Request.getInstance().delete(
        `/roles/${roleName}`,
        getRequestConfig({ addEnvDomain: true })
      ),
    () => true
  );

type DeleteOrganizationRoleRequest = DeleteRoleRequest & {
  orgId: string;
  reassignRoleName?: string;
};

const deleteOrganizationRole: (
  request: DeleteOrganizationRoleRequest
) => Promise<boolean> = withMakeRequest(
  async ({
    roleName,
    orgId,
    reassignRoleName
  }: DeleteOrganizationRoleRequest) =>
    await Request.getInstance().delete(
      `/organizations/${orgId}/roles/${roleName}${reassignRoleName ? `?reassign_role_name=${reassignRoleName}` : ''}`,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

const deleteAndReassignEnvironmentRole: (
  request: DeleteRoleRequest & { reassignRoleName: string }
) => Promise<RoleData> = withMakeRequest(
  async ({
    roleName,
    reassignRoleName
  }: DeleteRoleRequest & { reassignRoleName: string }) =>
    await Request.getInstance().delete(
      `/roles/${roleName}?reassign_role_name=${reassignRoleName}`,
      getRequestConfig({ addEnvDomain: true })
    )
);

const updateDirectoryRoleMapping: (
  request: UpdateDirectoryRoleMappingRequest
) => Promise<boolean> = withMakeRequest(
  async ({
    organizationId,
    directoryId,
    payload
  }: UpdateDirectoryRoleMappingRequest) =>
    await Request.getInstance().put(
      `/organizations/${organizationId}/directories/${directoryId}/groups/-/roles:assign`,
      payload,
      getRequestConfig({ addEnvDomain: true })
    ),
  (_) => true
);

const updateDefaultUserRoles: (request: {
  defaultCreatorRole: string;
  defaultMemberRole: string;
}) => Promise<any> = withMakeRequest(
  async (request) =>
    await Request.getInstance().patch(
      `/roles:set_defaults`,
      request,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data) => data
);

const getUsersPerRoleCount: (request: { roleName: string }) => Promise<number> =
  withMakeRequest(
    async ({ roleName }) =>
      await Request.getInstance().get(
        `/roles/${roleName}/users:count`,
        getRequestConfig({ addEnvDomain: true })
      ),
    (data: { count: string }) => Number(data.count)
  );

const getOrganizationRoleUsersCount: (request: {
  roleName: string;
  orgId: string;
}) => Promise<number> = withMakeRequest(
  async ({ roleName, orgId }) =>
    await Request.getInstance().get(
      `/organizations/${orgId}/roles/${roleName}/users:count`,
      getRequestConfig({ addEnvDomain: true })
    ),
  (data: { count: string }) => Number(data.count)
);

const deleteParentRole: (roleName) => Promise<boolean> = withMakeRequest(
  async (roleName) =>
    await Request.getInstance().delete(
      `/roles/${roleName}/base`,
      getRequestConfig({ addEnvDomain: true })
    ),
  () => true
);

export {
  getEnvironmentRoles,
  getEnvironmentRolesWithPermissions,
  getOrganizationRolesWithPermissions,
  getEnvironmentRoleDetails,
  getOrganizationRoleDetails,
  deleteParentRole,
  getOrganizationRoles,
  createEnvironmentRole,
  createOrganizationRole,
  updateEnvironmentRole,
  updateOrganizationRole,
  updateDefaultOrganizationRole,
  deleteEnvironmentRole,
  deleteOrganizationRole,
  updateDirectoryRoleMapping,
  updateDefaultUserRoles,
  getUsersPerRoleCount,
  getOrganizationRoleUsersCount,
  deleteAndReassignEnvironmentRole
};
